import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';

import { ApolloError, gql, useSubscription } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ChakraBox,
  ChakraFlex,
  ChakraText,
  EnvelopeUnread,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  FAIconName,
  InfiniteScroll,
  NavItem,
  Popover,
} from '@equip.health/ui';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ApiCommand } from '~/lib/Api';
import { VITE_NOTIFICATION_PAGE_SIZE } from '~/lib/constants/env';
import {
  NotificationEventType,
  NotificationStatus,
} from '~/lib/constants/notifications.constants';
import urlConstants from '~/lib/constants/url.constants';
import { useApi } from '~/lib/hooks';
import { getChannelName } from '~/lib/util/chat/chatChannel.util';
import {
  READ,
  StatusType,
  UNREAD,
  calculateFilteredCounts,
  combineSenderSupportChannelMembers,
  filterChatNotifications,
  getNotificationIdsToUpdate,
  isReadNotificationStatus,
  isChatNotification,
  isUnreadNotificationStatus,
  mergeFetchedNotifications,
  statusToCategoryMapper,
  updateMultipleNotifications,
  updateSingleNotification,
  upsertGroupedChatNotification,
  upsertNotificationByExternalId,
} from '~/lib/util/notification.util';
import { getPreferredFullName } from '~/lib/util/patient.util';
import util from '~/lib/util/util';
import Loading from '~/pages/loading';

import NotificationItem from './NotificationItem';
import { useDatadog } from '~/lib/hooks/useDatadog';

const NOTIFICATION_PAGE_SIZE = parseInt(VITE_NOTIFICATION_PAGE_SIZE, 10) || 200;

const { h5, label, labelBold } = EquipUITheme.typography;
const {
  tertiary: { marigold: brandTertiaryMarigold },
  neutral: {
    background: { secondary: neutralBgSecondary },
    tertiary: neutralTertiary,
  },
  primary,
} = EquipUIFireflyV2Theme.colors;

const TOP_LEVEL_ID = 'notification-menu';

const statusHoverWrapperBorderMapper: Record<StatusType, string> = {
  [READ]: `1px solid ${neutralTertiary}`,
  [UNREAD]: `1px solid ${brandTertiaryMarigold.base} !important`,
};
const statusWrapperBorderMapper: Record<StatusType, string> = {
  [READ]: `1px solid ${neutralBgSecondary}`,
  [UNREAD]: `1px solid ${brandTertiaryMarigold.background}`,
};

const GET_NOTIFICATION_BY_ID_SUBSCRIPTION = gql`
  subscription SubscribeToDataById($userAuthId: String!) {
    onPublishNotificationById(userAuthId: $userAuthId) {
      notificationExternalId
      userAuthId
      subject
      eventType
      groupBy
      senderId
      sender
      data
      priority
      action
      source
      createdAt
      status
      updatedAt
    }
  }
`;

interface NotificationsMenuProps {
  onNotificationsMenuVisibilityChange: (isNotificationOver: boolean) => void;
  isSmall?: boolean;
}

type NotificationsMenuState = {
  notifications: NotificationType[];
  totalCount: number;
  totalUnreadCount: number;
};

const NotificationsMenu = ({
  onNotificationsMenuVisibilityChange,
  isSmall = false,
}: NotificationsMenuProps) => {
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [notificationsMenuState, setNotificationsMenuState] =
    useState<NotificationsMenuState>({
      notifications: [],
      totalCount: 0,
      totalUnreadCount: 0,
    });
  const { notifications, totalCount, totalUnreadCount } =
    notificationsMenuState;
  const {
    user: { sub: userAuthId },
  } = useAuth0();
  const { logError } = useDatadog();

  const {
    maudProviderChatMessagesPrototype:
      isMaudProviderChatMessagesPrototypeEnabled,
  } = useFlags<LDFlags>();

  const { data: newNotification } = useSubscription(
    GET_NOTIFICATION_BY_ID_SUBSCRIPTION,
    {
      onError: (error: ApolloError) => {
        console.log(`AppSync onError: ${error && JSON.stringify(error)}`);
        logError(error, { logFrom: 'Apollo AppSync onError' });
      },
      shouldResubscribe: true,
      variables: { userAuthId },
    },
  );

  const appSyncNotification = useMemo(() => {
    const { onPublishNotificationById } = newNotification ?? {};

    if (!onPublishNotificationById) return null;

    const { data, sender, subject } = onPublishNotificationById;

    return {
      ...onPublishNotificationById,
      data: util.parseJson(data),
      sender: util.parseJson(sender),
      subject: util.parseJson(subject),
    };
  }, [newNotification]);

  const {
    data: notificationsRes,
    loading,
    sendRequest: getNotifications,
  } = useApi<GetNotificationsResponse>();

  const { sendRequest: updateNotification } =
    useApi<GetNotificationsResponse>();

  const fetchNotifications = (pageNumber: number, pageSize: number): void => {
    getNotifications({
      command: ApiCommand.GET,
      options: {
        'page-number': pageNumber,
        'page-size': pageSize,
      },
      url: urlConstants.notifications.getNotifications,
    });
  };

  const viewOldHandler = (): void => {
    if (loading || pageNumber * NOTIFICATION_PAGE_SIZE > totalCount) return;
    fetchNotifications(pageNumber + 1, NOTIFICATION_PAGE_SIZE);
    setPageNumber((pre) => pre + 1);
  };

  const onUpdateNotification = useCallback(
    (
      notificationExternalIds: Array<string>,
      status: NotificationStatus,
      isAll?: boolean,
    ): void => {
      if (notificationExternalIds.length === 0) return;
      updateNotification({
        command: ApiCommand.PUT,
        options: {
          notificationExternalIds,
          status,
        },
        url: urlConstants.notifications.updateNotification,
      });
      setNotificationsMenuState((prev) => {
        if (isAll) {
          const notifications = updateMultipleNotifications(
            notificationExternalIds,
            isReadNotificationStatus(status)
              ? [
                  NotificationStatus.NEW,
                  NotificationStatus.UNREAD,
                  NotificationStatus.DELIVERED,
                ]
              : [NotificationStatus.READ, NotificationStatus.ARCHIVED],
            status,
            prev.notifications,
          );

          return {
            ...prev,
            notifications,
            totalUnreadCount: isReadNotificationStatus(status)
              ? 0
              : notifications.length,
          };
        }

        const currentNotification = prev.notifications.find(
          (notification) =>
            notification.notificationExternalId === notificationExternalIds[0],
        );

        return {
          ...prev,
          notifications: updateSingleNotification(
            notificationExternalIds,
            status,
            prev.notifications,
          ),
          totalUnreadCount: Math.max(
            prev.totalUnreadCount +
              (currentNotification
                ? Number(isUnreadNotificationStatus(status)) -
                  Number(isUnreadNotificationStatus(currentNotification.status))
                : 0),
            0,
          ),
        };
      });
    },
    [updateNotification],
  );

  useEffect(() => {
    fetchNotifications(pageNumber, NOTIFICATION_PAGE_SIZE);
  }, []);

  useEffect(() => {
    if (!notificationsRes?.data) return;

    const filteredNotifications = filterChatNotifications(
      notificationsRes.data,
      isMaudProviderChatMessagesPrototypeEnabled,
    );
    const { adjustedTotalCount, adjustedUnreadCount } = calculateFilteredCounts(
      notificationsRes.data,
      notificationsRes.totalCount,
      notificationsRes.totalUnreadCount,
      isMaudProviderChatMessagesPrototypeEnabled,
    );

    setNotificationsMenuState((prev) => ({
      notifications: mergeFetchedNotifications(
        prev.notifications,
        filteredNotifications,
      ),
      totalCount: adjustedTotalCount,
      totalUnreadCount: adjustedUnreadCount,
    }));
  }, [notificationsRes, isMaudProviderChatMessagesPrototypeEnabled]);

  useEffect(() => {
    onNotificationsMenuVisibilityChange(isSelected);
  }, [isSelected, onNotificationsMenuVisibilityChange]);

  useEffect(() => {
    if (!appSyncNotification) return;

    if (
      isMaudProviderChatMessagesPrototypeEnabled &&
      isChatNotification(appSyncNotification.eventType as NotificationEventType)
    ) {
      return;
    }

    setNotificationsMenuState((prev) => {
      const notificationUpdate = isChatNotification(
        appSyncNotification.eventType as NotificationEventType,
      )
        ? upsertGroupedChatNotification(
            appSyncNotification,
            prev.notifications,
            NOTIFICATION_PAGE_SIZE,
          )
        : upsertNotificationByExternalId(
            appSyncNotification,
            prev.notifications,
            NOTIFICATION_PAGE_SIZE,
          );

      return {
        notifications: notificationUpdate.notifications,
        totalCount: Math.max(
          prev.totalCount + notificationUpdate.totalDelta,
          0,
        ),
        totalUnreadCount: Math.max(
          prev.totalUnreadCount + notificationUpdate.unreadDelta,
          0,
        ),
      };
    });
  }, [appSyncNotification, isMaudProviderChatMessagesPrototypeEnabled]);

  const triggerContentRenderer = () => (
    <ChakraBox
      id={`${TOP_LEVEL_ID}__trigger-content`}
      padding="0px 4px 12px 4px"
    >
      <NavItem
        counter={totalUnreadCount > 0 && totalUnreadCount}
        iconName={FAIconName.bellRegular}
        isSelected={isSelected}
        isSmall={isSmall}
        label="Notifications"
        tooltipText="Notifications"
      />
    </ChakraBox>
  );

  const headerContentRenderer = () => (
    <ChakraFlex
      alignItems="center"
      id={`${TOP_LEVEL_ID}__header-conetent`}
      justifyContent="space-between"
    >
      <ChakraText {...h5}>Notifications</ChakraText>
    </ChakraFlex>
  );

  const bodyContentRenderer = useMemo(() => {
    const displayNotifications = filterChatNotifications(
      notifications,
      isMaudProviderChatMessagesPrototypeEnabled,
    );

    if (displayNotifications.length === 0)
      return (
        <ChakraText padding="10px" textAlign="center">
          No Notifications
        </ChakraText>
      );

    const notificationsByStatus: Record<StatusType, ReactElement[]> = {
      [READ]: [],
      [UNREAD]: [],
    };

    for (const notification of displayNotifications) {
      const {
        action,
        createdAt,
        data,
        eventType,
        groupBy,
        notificationExternalId,
        status,
        subject,
        relatedNotifications,
      } = notification;
      const isChatEvent: boolean =
        notification?.eventType === 'CHAT_MENTION' ||
        notification?.eventType === 'CHAT_REPLY' ||
        notification?.eventType === 'CHAT_MESSAGE';
      const groupChannel = {
        members: combineSenderSupportChannelMembers(data),
        url: data?.channelUri,
      } as GroupChannel;
      const channelName = isChatEvent
        ? getChannelName(groupChannel)
        : (data?.channelName ?? data?.careTeamName ?? data?.care_team_name);
      const category = statusToCategoryMapper[status];

      notificationsByStatus[category].push(
        <NotificationItem
          action={action}
          channelName={channelName}
          createdAt={createdAt}
          data={data}
          dob={data?.dob}
          eventType={eventType as NotificationEventType}
          groupBy={groupBy}
          hoverWrapperBorder={statusHoverWrapperBorderMapper[category]}
          isRead={category === READ}
          key={`notifications-menu-item-${notificationExternalId}`}
          newStatus={data?.new_status}
          notificationExternalId={notificationExternalId}
          oldStatus={data?.old_status}
          onClose={() => setIsSelected(false)}
          onUpdateNotification={(
            notificationExternalIds: Array<string>,
            status: NotificationStatus,
          ) => onUpdateNotification(notificationExternalIds, status)}
          patientName={getPreferredFullName(
            subject?.chosenName,
            subject?.firstName,
            subject?.lastName,
          )}
          relatedNotifications={relatedNotifications ?? []}
          subjectId={subject?.externalId}
          type={data?.type}
          wrapperBackgroundColor={
            category === READ ? undefined : brandTertiaryMarigold.background
          }
          wrapperBorder={statusWrapperBorderMapper[category]}
        />,
      );
    }

    return (
      <ChakraBox id={`${TOP_LEVEL_ID}__body-content`} padding="0">
        {notificationsByStatus[UNREAD].length > 0 && (
          <>
            <ChakraFlex
              justifyContent="center"
              marginTop="8px"
              position="relative"
            >
              <ChakraText
                {...label}
                color="neutral.secondary"
                textAlign="center"
              >
                Unread
              </ChakraText>
              <ChakraFlex
                cursor="pointer"
                onClick={() =>
                  onUpdateNotification(
                    displayNotifications
                      .filter((item) => isUnreadNotificationStatus(item.status))
                      .reduce((prev: string[], current: NotificationType) => {
                        return prev.concat([
                          current.notificationExternalId,
                          ...getNotificationIdsToUpdate(
                            NotificationStatus.READ,
                            current.relatedNotifications,
                          ),
                        ]);
                      }, []),
                    NotificationStatus.READ,
                    true,
                  )
                }
                position="absolute"
                right="2"
                top="0"
              >
                <ChakraText
                  {...labelBold}
                  color={primary[100]}
                  paddingRight="4px"
                >
                  Mark all as read
                </ChakraText>
                <EnvelopeUnread
                  fill={primary[100]}
                  height="15px"
                  width="15px"
                />
              </ChakraFlex>
            </ChakraFlex>
            {notificationsByStatus[UNREAD]}
          </>
        )}
        {notificationsByStatus[READ].length > 0 && (
          <>
            <ChakraText {...label} color="neutral.secondary" textAlign="center">
              Read
            </ChakraText>
            {notificationsByStatus[READ]}
          </>
        )}
        {loading && <Loading />}
      </ChakraBox>
    );
  }, [
    notifications,
    loading,
    isMaudProviderChatMessagesPrototypeEnabled,
    onUpdateNotification,
  ]);

  const footerContentRenderer = () => {
    if (notifications.length === 0) return null;
    return (
      <ChakraBox id={`${TOP_LEVEL_ID}__footer-content`}>
        <ChakraText
          {...label}
          color="neutral.secondary"
          id={`${TOP_LEVEL_ID}__footer-showing-count`}
          padding="4px"
          textAlign="center"
        >
          Showing 1 -{' '}
          {pageNumber * NOTIFICATION_PAGE_SIZE <= totalCount
            ? pageNumber * NOTIFICATION_PAGE_SIZE
            : totalCount}
        </ChakraText>
      </ChakraBox>
    );
  };

  return (
    <Popover
      bodyContent={
        <InfiniteScroll
          dataLength={notifications.length}
          fetchData={viewOldHandler}
          hasMore={pageNumber * NOTIFICATION_PAGE_SIZE < totalCount}
          height="474px"
          hideCount
          id={`${TOP_LEVEL_ID}__infinite_scroll_wrapper`}
          totalCount={totalCount}
        >
          {bodyContentRenderer}
        </InfiniteScroll>
      }
      bodyPadding="0px"
      footerContent={footerContentRenderer()}
      headerContent={headerContentRenderer()}
      id={TOP_LEVEL_ID}
      isOpen={isSelected}
      margin="0px 0px 20px 0px"
      onClose={() => setIsSelected(false)}
      onOpen={() => setIsSelected(true)}
      placement="right"
      size="lg"
      triggerContent={triggerContentRenderer()}
    />
  );
};

export default NotificationsMenu;
